/** Sales party settlement review rows — same layout language as purchase SettlementReviewTable. */
import { formatInr, formatQtl } from "./accountsSettlement";

/**
 * Truck-level summary rows for sales finalization step 2.
 */
export function buildSalesTruckReviewRows(totals = {}, form = {}) {
  const rows = [
    {
      key: "billable",
      particular: "Total billable weight",
      condition: "After bardana / bag cut and quality weight cut",
      conditionEdit: null,
      contractValue: "From weighbridge + contract",
      edit: null,
      final: formatQtl(totals.billable_qtl),
      highlight: true,
    },
    {
      key: "full_amount",
      particular: "Full amount",
      condition: "Billable × rate (before CD / brokerage / adjustments)",
      conditionEdit: null,
      contractValue: "—",
      edit: null,
      final: formatInr(totals.full_amount),
      highlight: true,
    },
    {
      key: "cd",
      particular: "Cash discount (CD) total",
      condition: "From sales contract — editable before posting",
      conditionEdit: { type: "cd_root" },
      contractValue: totals.cd_percent != null ? `${totals.cd_percent}%` : "—",
      edit: null,
      final: `−${formatInr(totals.cd_amount || 0)}`,
    },
    {
      key: "brokerage",
      particular: "Brokerage total",
      condition: "From sales contract — our-side vs deducted from buyer",
      conditionEdit: { type: "brokerage_root" },
      contractValue: totals.brokerage_rate != null ? String(totals.brokerage_rate) : "—",
      edit: null,
      final: `−${formatInr(totals.brokerage_amount || 0)}`,
    },
    {
      key: "gst",
      particular: "GST",
      condition: "CGST/SGST or IGST on net after deductions",
      conditionEdit: { type: "gst_root" },
      contractValue: totals.gst_type && totals.gst_type !== "NONE" ? `${totals.gst_type} ${totals.gst_rate || 0}%` : "Not applied",
      edit: null,
      final: formatInr(totals.gst_amount || 0),
    },
    {
      key: "before_adj",
      particular: "Buyer receivable (before adjustments)",
      condition: "After CD / brokerage",
      conditionEdit: null,
      contractValue: "—",
      edit: null,
      final: formatInr(totals.receivable_before_adjustments ?? totals.receivable),
      highlight: true,
    },
    {
      key: "other_ded",
      particular: "Other deductions (from party)",
      condition: "Accountant lines below",
      conditionEdit: null,
      contractValue: "—",
      edit: null,
      final: `−${formatInr(totals.other_deductions_total || 0)}`,
    },
    {
      key: "other_add",
      particular: "Other additions (charged on party)",
      condition: "Accountant lines below",
      conditionEdit: null,
      contractValue: "—",
      edit: null,
      final: `+${formatInr(totals.other_additions_total || 0)}`,
    },
    {
      key: "receivable",
      particular: "Buyer receivable (final)",
      condition: "Before adjustments − deductions + additions",
      conditionEdit: null,
      contractValue: "—",
      edit: null,
      final: formatInr(totals.receivable),
      highlight: true,
      total: true,
    },
  ];

  if (form.accounts_note) {
    rows.push({
      key: "note",
      particular: "Accounts note",
      condition: "Saved with voucher",
      conditionEdit: null,
      contractValue: form.accounts_note,
      edit: null,
      final: "—",
    });
  }

  return rows;
}

/**
 * Per-contract sales settlement review — mirrors purchase buildSettlementReviewRows.
 */
export function buildSalesSettlementReviewRows(settlement, form = {}, { isBran = false } = {}) {
  const rows = [];
  const net = settlement.net_qtl;
  const billable = settlement.billable_qtl;
  const bagCut = settlement.bag_cut_qtl || 0;
  const bardanaLabel = settlement.bardana_gm
    ? `${settlement.bardana_gm} gm ${settlement.bardana_basis === "PER_QTL" ? "per qtl" : "per bag"}`
    : (settlement.apply_bag_cuts ? "Bag cut / bardana from contract" : "No bag cut applied");

  rows.push({
    key: "bags",
    particular: "Loaded bags",
    condition: "Allocated to this contract",
    conditionEdit: null,
    contractValue: String(settlement.bags ?? "—"),
    edit: null,
    final: String(settlement.bags ?? "—"),
  });

  rows.push({
    key: "net_qtl",
    particular: "Weighbridge net",
    condition: "Gross − tare (scoped to contract)",
    conditionEdit: null,
    contractValue: formatQtl(net),
    edit: null,
    final: formatQtl(net),
  });

  rows.push({
    key: "bardana",
    particular: "Bardana / bag-cut deduction",
    condition: bardanaLabel,
    conditionEdit: { type: "bardana_root" },
    contractValue: formatQtl(bagCut),
    edit: null,
    final: bagCut > 0 ? `−${formatQtl(bagCut)}` : formatQtl(0),
  });

  rows.push({
    key: "weight_cut",
    particular: "Quality weight cut",
    condition: "Negotiation on billable qtl",
    conditionEdit: null,
    contractValue: "0%",
    edit: { field: "weight_cut_pct", type: "number", step: "0.1", min: 0 },
    final: `${Number(form.weight_cut_pct || settlement.weight_cut_pct || 0)}% → ${formatQtl(billable)} billable`,
  });

  rows.push({
    key: "billable",
    particular: "Billable weight",
    condition: "Net after bardana and quality weight cut",
    conditionEdit: null,
    contractValue: formatQtl(billable),
    edit: null,
    final: formatQtl(billable),
    highlight: true,
  });

  rows.push({
    key: "rate",
    particular: "Rate",
    condition: "Sales contract rate (override if needed)",
    conditionEdit: null,
    contractValue: `${formatInr(settlement.full_rate)}/qtl`,
    edit: { field: "revised_rate", type: "number", step: "0.01", min: 0 },
    final: `${formatInr(settlement.effective_rate ?? settlement.full_rate)}/qtl effective`,
  });

  {
    const basis = (form.price_cut_basis || settlement.price_cut_basis || "GROSS_PCT").toUpperCase();
    const cutVal = Number(form.price_cut_pct ?? settlement.price_cut_pct ?? 0);
    const cutLabel = basis === "PER_QTL"
      ? `${formatInr(cutVal)}/qtl`
      : `${cutVal}% of gross`;
    const ded = settlement.price_cut_deduction;
    rows.push({
      key: "price_cut",
      particular: "Quality price cut",
      condition: basis === "PER_QTL" ? "₹ per billable qtl" : "% of gross (after FFA if any)",
      conditionEdit: { type: "select", field: "price_cut_basis" },
      contractValue: "None",
      edit: {
        field: "price_cut_pct",
        type: "number",
        step: "0.01",
        min: 0,
        placeholder: basis === "PER_QTL" ? "₹/qtl" : "%",
      },
      final: ded != null && Number(ded) > 0
        ? `−${formatInr(ded)} (${cutLabel})`
        : cutLabel,
    });
  }

  rows.push({
    key: "gross",
    particular: "Full / gross amount",
    condition: "Billable × rate",
    conditionEdit: null,
    contractValue: formatInr(settlement.full_amount),
    edit: null,
    final: formatInr(settlement.full_amount),
    highlight: true,
  });

  if (isBran && settlement.ffa_amount != null) {
    rows.push({
      key: "ffa",
      particular: "FFA deduction",
      condition: settlement.lab_ffa != null ? `Lab FFA ${settlement.lab_ffa}%` : "Haryana FFA bands",
      conditionEdit: null,
      contractValue: "—",
      edit: null,
      final: `−${formatInr(settlement.ffa_amount || 0)}`,
    });
  }

  rows.push({
    key: "cd",
    particular: "Cash discount (CD)",
    condition: settlement.cd_applicable
      ? `${settlement.cd_percent}% on amount after cuts`
      : "Not applicable on contract",
    conditionEdit: { type: "cd_root" },
    contractValue: settlement.cd_applicable ? `${settlement.cd_percent}%` : "N/A",
    edit: null,
    final: settlement.cd_amount ? `−${formatInr(settlement.cd_amount)}` : "—",
  });

  rows.push({
    key: "brokerage",
    particular: "Brokerage",
    condition: settlement.brokerage_basis
      ? `${settlement.brokerage_basis}${settlement.brokerage_rate != null ? ` · ${settlement.brokerage_rate}` : ""}`
      : "From sales contract",
    conditionEdit: { type: "brokerage_root" },
    contractValue: settlement.brokerage_rate != null ? String(settlement.brokerage_rate) : "—",
    edit: null,
    final: settlement.brokerage_amount ? `−${formatInr(settlement.brokerage_amount)}` : "—",
  });

  rows.push({
    key: "line_recv",
    particular: "Line buyer receivable",
    condition: "Gross − CD (− brokerage if deducted from receivable)",
    conditionEdit: null,
    contractValue: "—",
    edit: null,
    final: formatInr(settlement.buyer_receivable),
    highlight: true,
    total: true,
  });

  return rows;
}
